import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cocktailApi, Cocktail } from '../services/cocktailApi.ts';
import { FavoritesService } from '../services/favoritesService.ts';
import { validateCocktailId } from '../utils/validation.ts';
import './CocktailDetail.css';

const CocktailDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cocktail, setCocktail] = useState<Cocktail | null>(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validatedId = validateCocktailId(id);
    if (validatedId) {
      loadCocktail(validatedId);
    } else {
      setError('Invalid cocktail ID');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (cocktail) {
      setIsFavorite(FavoritesService.isFavorite(cocktail.idDrink));
    }
  }, [cocktail]);

  const loadCocktail = async (cocktailId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await cocktailApi.getCocktailById(cocktailId);
      if (data) {
        setCocktail(data);
      } else {
        setError('Cocktail not found');
      }
    } catch (err) {
      setError('Failed to load cocktail details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = () => {
    if (cocktail) {
      const newFavoriteStatus = FavoritesService.toggleFavorite(cocktail.idDrink);
      setIsFavorite(newFavoriteStatus);
    }
  };

  const getIngredients = (cocktail: Cocktail) => {
    const ingredients: { ingredient: string; measure: string }[] = [];
    for (let i = 1; i <= 15; i++) {
      const ingredient = cocktail[`strIngredient${i}` as keyof Cocktail] as string;
      const measure = cocktail[`strMeasure${i}` as keyof Cocktail] as string;
      
      if (ingredient && ingredient.trim()) {
        ingredients.push({
          ingredient,
          measure: measure || ''
        });
      }
    }
    return ingredients;
  };

  if (loading) {
    return <div className="loading">Loading cocktail details...</div>;
  }

  if (error || !cocktail) {
    return (
      <div className="error">
        {error || 'Cocktail not found'}
        <button onClick={() => navigate('/')} style={{ marginTop: '10px', padding: '8px 16px' }}>
          Go Home
        </button>
      </div>
    );
  }

  const ingredients = getIngredients(cocktail);

  return (
    <div className="cocktail-detail">
      <div className="cocktail-header">
        <img 
          src={cocktail.strDrinkThumb} 
          alt={cocktail.strDrink} 
          className="cocktail-detail-image"
        />
        <div className="cocktail-info">
          <h1>{cocktail.strDrink}</h1>
          <p><strong>Category:</strong> {cocktail.strCategory}</p>
          <p><strong>Type:</strong> {cocktail.strAlcoholic}</p>
          <p><strong>Glass:</strong> {cocktail.strGlass}</p>
          <button
            onClick={handleToggleFavorite}
            className={`favorite-btn ${isFavorite ? 'favorited' : ''}`}
          >
            {isFavorite ? '❤️ Remove from Favorites' : '🤍 Add to Favorites'}
          </button>
        </div>
      </div>

      <div className="cocktail-content">
        <div className="ingredients">
          <h2>Ingredients</h2>
          <ul>
            {ingredients.map((item, index) => (
              <li key={index}>
                {item.measure} {item.ingredient}
              </li>
            ))}
          </ul>
        </div>

        <div className="instructions">
          <h2>Instructions</h2>
          <p>{cocktail.strInstructions}</p>
        </div>
      </div>
    </div>
  );
};

export default CocktailDetail;
